import {
  Avatar,
  Button,
  Chip,
  LinearProgress,
  TextField,
} from "@material-ui/core";
import {
  AddCircleOutline,
  AssignmentTurnedIn,
  BarChart,
  CheckCircleOutline,
  Dashboard as DashboardIcon,
  ErrorOutline,
  ExitToApp,
  People,
  PlayArrow,
  Poll,
  QuestionAnswer,
  RateReview,
  RecordVoiceOver,
  School,
  Search,
  Settings,
} from "@material-ui/icons";
import { getUserRole } from "./service";
import "./App.css";

const adminNav = [
  ["Portfolio", DashboardIcon],
  ["Clients", People],
  ["Failed Conversations", ErrorOutline],
  ["Training Queue", School],
  ["Voice Answers", QuestionAnswer],
  ["Analytics", BarChart],
  ["Demos", PlayArrow],
  ["Reviews", RateReview],
  ["Surveys", Poll],
  ["Settings", Settings],
];

const clientNav = [
  ["Overview", DashboardIcon],
  ["Voice Answers", QuestionAnswer],
  ["Failed Questions", ErrorOutline],
  ["Train Assistant", School],
  ["Analytics", BarChart],
  ["Channels", RecordVoiceOver],
  ["Reviews", RateReview],
  ["Surveys", Poll],
  ["Settings", Settings],
];

const clients = [
  {
    name: "Lisbon Dental Studio",
    category: "Healthcare",
    channels: "Alexa, Google",
    engagement: "842",
    success: "91%",
    failed: 18,
    training: "Needs approval",
    next: "Approve 7 intents",
  },
  {
    name: "Cascais Surf School",
    category: "Lessons",
    channels: "Alexa, Siri",
    engagement: "436",
    success: "86%",
    failed: 31,
    training: "Draft answers",
    next: "Resolve missed pricing questions",
  },
  {
    name: "Porto Vegan Kitchen",
    category: "Restaurant",
    channels: "Google",
    engagement: "1,284",
    success: "94%",
    failed: 9,
    training: "Published",
    next: "Review promotion survey",
  },
  {
    name: "Algarve Bike Tours",
    category: "Tours",
    channels: "Alexa, Google",
    engagement: "517",
    success: "89%",
    failed: 22,
    training: "Training",
    next: "Publish opening-hours update",
  },
];

const clientQuestions = [
  ["Do you have late appointments?", "Add answer", "high"],
  ["Can I park nearby?", "Train", "medium"],
  ["Are summer promotions active?", "Add answer", "high"],
  ["Do you accept walk-ins?", "Ignore", "low"],
];

const answers = [
  ["Opening hours", "We are open Monday to Friday, 9:00 to 18:00."],
  ["Location", "We are located near the main square with parking nearby."],
  ["Promotions", "This month, new customers receive 15% off first service."],
];

const Stat = ({ label, value, helper }) => (
  <div className="bv-stat">
    <span>{label}</span>
    <strong>{value}</strong>
    {helper ? <small>{helper}</small> : null}
  </div>
);

const Sidebar = ({ items, role }) => (
  <aside className="bv-sidebar">
    <div className="bv-brand">
      <Avatar className="bv-brand-mark">BV</Avatar>
      <div>
        <strong>BonoVox</strong>
        <span>{role === "admin" ? "Admin" : "Client"}</span>
      </div>
    </div>
    <nav className="bv-nav">
      {items.map(([label, Icon], index) => (
        <button
          className={index === 0 ? "bv-nav-item active" : "bv-nav-item"}
          key={label}
          type="button"
        >
          <Icon fontSize="small" />
          <span>{label}</span>
        </button>
      ))}
    </nav>
  </aside>
);

const Header = ({ title, subtitle, role }) => (
  <header className="bv-header">
    <div>
      <Chip
        className="bv-role-chip"
        label={role === "admin" ? "Admin credentials" : "Client credentials"}
        size="small"
      />
      <h1>{title}</h1>
      <p>{subtitle}</p>
    </div>
    <div className="bv-header-actions">
      <div className="bv-search">
        <Search fontSize="small" />
        <span>Search</span>
      </div>
      {role === "admin" ? (
        <Button color="primary" startIcon={<AddCircleOutline />} variant="contained">
          Add client
        </Button>
      ) : (
        <Button color="primary" startIcon={<PlayArrow />} variant="contained">
          Test assistant
        </Button>
      )}
      <Button
        className="bv-logout"
        onClick={() => {
          localStorage.clear();
          window.location = "https://www.bonovox.net";
        }}
        startIcon={<ExitToApp />}
      >
        Logout
      </Button>
    </div>
  </header>
);

const AdminDashboard = () => (
  <div className="bv-page">
    <Sidebar items={adminNav} role="admin" />
    <main className="bv-main">
      <Header
        role="admin"
        title="Admin portfolio"
        subtitle="Manage client voice-channel health, training queues, demos, reviews, and surveys."
      />
      <section className="bv-stats-grid">
        <Stat label="Active clients" value="38" helper="+6 this month" />
        <Stat label="Voice engagements" value="12.8k" helper="Across portfolio" />
        <Stat label="Unresolved misses" value="214" helper="41 high priority" />
        <Stat label="Survey responses" value="1,492" helper="4.6 average score" />
      </section>

      <section className="bv-content-grid admin">
        <div className="bv-panel portfolio-panel">
          <div className="bv-section-heading">
            <div>
              <h2>Client portfolio</h2>
              <p>Prioritized by unanswered demand and training status.</p>
            </div>
            <Button size="small">Export</Button>
          </div>
          <div className="bv-table">
            <div className="bv-table-row header">
              <span>Business</span>
              <span>Channels</span>
              <span>Engagement</span>
              <span>Success</span>
              <span>Failed</span>
              <span>Next action</span>
            </div>
            {clients.map((client) => (
              <div className="bv-table-row" key={client.name}>
                <span>
                  <strong>{client.name}</strong>
                  <small>{client.category}</small>
                </span>
                <span>{client.channels}</span>
                <span>{client.engagement}</span>
                <span>{client.success}</span>
                <span>
                  <Chip
                    className={client.failed > 20 ? "bv-chip warning" : "bv-chip"}
                    label={client.failed}
                    size="small"
                  />
                </span>
                <span>{client.next}</span>
              </div>
            ))}
          </div>
        </div>

        <aside className="bv-panel work-panel">
          <div className="bv-section-heading compact">
            <h2>Today's work</h2>
            <Chip label="12 open" size="small" />
          </div>
          {[
            ["Approve training", "7 new intents for Lisbon Dental Studio"],
            ["Answer missed question", "Cascais Surf School pricing query"],
            ["Review survey", "Porto Vegan Kitchen promotion feedback"],
            ["Follow up demo", "Algarve Bike Tours trial assistant"],
          ].map(([title, detail]) => (
            <div className="bv-work-item" key={title}>
              <CheckCircleOutline fontSize="small" />
              <div>
                <strong>{title}</strong>
                <span>{detail}</span>
              </div>
            </div>
          ))}
        </aside>
      </section>

      <section className="bv-bottom-grid">
        <div className="bv-panel">
          <div className="bv-section-heading compact">
            <h2>Demo and activation pipeline</h2>
            <Chip label="Try assistant" size="small" />
          </div>
          <div className="bv-pipeline">
            {[
              ["Trial assistants", "26", 72],
              ["Demos booked", "14", 52],
              ["Clients activated", "9", 38],
            ].map(([label, value, progress]) => (
              <div key={label}>
                <span>{label}</span>
                <strong>{value}</strong>
                <LinearProgress variant="determinate" value={progress} />
              </div>
            ))}
          </div>
        </div>
        <div className="bv-panel">
          <div className="bv-section-heading compact">
            <h2>Assistant preview</h2>
            <Chip label="Selected client" size="small" />
          </div>
          <div className="bv-assistant-box">
            <span>Customer asks</span>
            <strong>Do you have vegan lunch specials?</strong>
            <p>
              Yes. Today's lunch special is a vegan bowl with soup and fresh juice.
            </p>
          </div>
        </div>
      </section>
    </main>
  </div>
);

const ClientDashboard = () => (
  <div className="bv-page">
    <Sidebar items={clientNav} role="client" />
    <main className="bv-main">
      <Header
        role="client"
        title="Voice command center"
        subtitle="Improve what customers hear when they ask about your business."
      />
      <section className="bv-stats-grid">
        <Stat label="Questions asked" value="1,284" helper="+18% this month" />
        <Stat label="Answer success" value="94%" helper="Goal 96%" />
        <Stat label="Missed questions" value="23" helper="Needs review" />
        <Stat label="Survey score" value="4.7" helper="312 responses" />
      </section>

      <section className="bv-content-grid client">
        <div className="bv-panel">
          <div className="bv-section-heading">
            <div>
              <h2>Questions needing answers</h2>
              <p>Turn missed customer questions into trained voice answers.</p>
            </div>
            <Button color="primary" size="small" variant="outlined">
              Review all
            </Button>
          </div>
          <div className="bv-question-list">
            {clientQuestions.map(([question, action, priority]) => (
              <div className="bv-question-row" key={question}>
                <div>
                  <strong>{question}</strong>
                  <span>{priority} priority</span>
                </div>
                <Button color="primary" size="small">
                  {action}
                </Button>
              </div>
            ))}
          </div>
        </div>

        <aside className="bv-panel">
          <div className="bv-section-heading compact">
            <h2>Test your assistant</h2>
            <Chip label="Live preview" size="small" />
          </div>
          <TextField
            fullWidth
            label="Ask a customer question"
            size="small"
            value="What are your hours?"
            variant="outlined"
          />
          <div className="bv-assistant-box">
            <span>BonoVox responds</span>
            <p>We are open Monday to Friday, 9:00 to 18:00.</p>
          </div>
        </aside>
      </section>

      <section className="bv-bottom-grid client">
        <div className="bv-panel">
          <div className="bv-section-heading compact">
            <h2>Voice answers</h2>
            <Chip label="Train intent" size="small" />
          </div>
          <div className="bv-answer-list">
            {answers.map(([label, answer]) => (
              <div key={label}>
                <strong>{label}</strong>
                <span>{answer}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bv-panel">
          <div className="bv-section-heading compact">
            <h2>Reviews and surveys</h2>
            <Chip label="New module" size="small" />
          </div>
          <div className="bv-survey-summary">
            <AssignmentTurnedIn />
            <div>
              <strong>Post-interaction voice survey</strong>
              <span>Ask customers for satisfaction, review intent, and follow-up permission.</span>
            </div>
          </div>
          <div className="bv-survey-metrics">
            <span>Review requests: 128</span>
            <span>Survey completions: 312</span>
            <span>Promoter score: 71</span>
          </div>
        </div>
        <div className="bv-panel">
          <div className="bv-section-heading compact">
            <h2>Channel status</h2>
            <Chip label="Published" size="small" />
          </div>
          {["Alexa", "Google Voice", "Siri"].map((channel, index) => (
            <div className="bv-channel-row" key={channel}>
              <RecordVoiceOver fontSize="small" />
              <span>{channel}</span>
              <Chip label={index === 2 ? "Pending" : "Connected"} size="small" />
            </div>
          ))}
        </div>
      </section>
    </main>
  </div>
);

const Dashboard = () => {
  const role = getUserRole();
  return role === "admin" ? <AdminDashboard /> : <ClientDashboard />;
};

export default Dashboard;
